import React, { useState, useEffect } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { regions, provinces, cities } from 'select-philippines-address';
import { colors } from '@/constants/colors';
import Btn from '../UI/Btn';
import FileUploader from '../UI/FileUploader';
import { useUIStore } from '@/store/useUIStore';
import {
   submitDistributorApplication,
   listMyApplications,
   getMyResellerApi,
   getCreditPackageTiers,
   getCreditPackagePaymentDetails,
   type SubmitDistributorApplicationPayload,
   type DistributorQualifyingAnswers,
   type CreditPackageTier,
} from '@/api/vendo';
import { computeApprovalScore } from '@/utils/distributorApprovalScore';

// Step 2 of the wizard captures an advance credit-bundle purchase: the
// applicant picks a bundle, sends payment off-platform, and uploads proof.
// The buy executes when admin approves the application — so an approved
// distributor always starts with credits to sell.
type SelectedAccount =
   | { kind: 'bank'; index: number }
   | { kind: 'gcash'; index: number }
   | null;

type DocFieldKey =
   | 'govIdUrl'
   | 'mayorsPermitUrl'
   | 'bir2303Url'
   | 'proofOfAddressUrl';

const REQUIRED_DOCS: { key: DocFieldKey; label: string; hint: string }[] = [
   { key: 'govIdUrl', label: 'Government ID', hint: "Driver's license, passport, etc." },
];

const OPTIONAL_DOCS: { key: DocFieldKey; label: string; hint: string }[] = [
   { key: 'mayorsPermitUrl', label: "Mayor's permit", hint: 'Current year' },
   { key: 'bir2303Url', label: 'BIR 2303', hint: 'Certificate of registration' },
   { key: 'proofOfAddressUrl', label: 'Proof of address', hint: 'Utility bill, lease, etc.' },
];

type QualifyingKey = keyof DistributorQualifyingAnswers;

const QUALIFYING_QUESTIONS: { key: QualifyingKey; label: string }[] = [
   { key: 'hasPisoWifiExperience', label: 'Do you have experience in building piso WiFi before?' },
   { key: 'hasMikrotikKnowledge', label: 'Do you have knowledge in MikroTik routers / RouterOS?' },
   { key: 'isPriorDistributor', label: 'Are you previously a Distributor of another piso WiFi system?' },
   { key: 'hasPhysicalLocation', label: 'Do you have a physical store or office location?' },
   { key: 'willProvideSupport', label: 'Are you willing to provide technical support to your buyers?' },
   { key: 'hasWaitingCustomers', label: 'Do you currently have customers waiting to buy?' },
   { key: 'willSellInOwnRegion', label: 'Will you sell mainly in your own region?' },
   { key: 'hasOnlinePresence', label: 'Do you have a Facebook page or online presence for marketing?' },
   { key: 'canCommitMonthly', label: 'Can you commit to buying credits regularly (at least monthly)?' },
   { key: 'willDoOwnSetup', label: 'Will you be doing the technical setup yourself for buyers?' },
];

const emptyAnswers: DistributorQualifyingAnswers = {
   hasPisoWifiExperience: false,
   hasMikrotikKnowledge: false,
   isPriorDistributor: false,
   hasPhysicalLocation: false,
   willProvideSupport: false,
   hasWaitingCustomers: false,
   willSellInOwnRegion: false,
   hasOnlinePresence: false,
   canCommitMonthly: false,
   willDoOwnSetup: false,
};

// Persist the in-progress application so an accidental modal close doesn't wipe
// everything the operator typed. Cleared on successful submit.
const DRAFT_KEY = 'smartfi.distributor-apply-draft';
const loadDraft = (): any => {
   try {
      return JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
   } catch {
      return null;
   }
};

const EMPTY_FORM = {
   displayName: '',
   businessName: '',
   region: '',
   city: '',
   contactPhone: '',
   facebookUrl: '',
   govIdUrl: '',
   mayorsPermitUrl: '',
   bir2303Url: '',
   proofOfAddressUrl: '',
};
type FormShape = typeof EMPTY_FORM;

const DistributorApply = () => {
   const { handleClose } = useUIStore();
   const queryClient = useQueryClient();

   const { data: myApplications = [] } = useQuery({
      queryKey: ['vendo', 'distributor-applications', 'mine'],
      queryFn: listMyApplications,
   });
   const pendingExisting = myApplications.find((a) => a.status === 'pending');

   // A reseller filling this in is UPGRADING: on approval the account
   // converts and they stop being a reseller. Nobody should find that out
   // afterwards, so the form says it plainly before they submit.
   const { data: myResellerData } = useQuery({
      queryKey: ['vendo', 'reseller', 'me'],
      queryFn: getMyResellerApi,
   });
   const isUpgrade = myResellerData?.reseller?.status === 'approved';

   // Form state: keep optional doc fields as empty strings locally; we
   // only convert empty → undefined when submitting so the server-side
   // schema treats them as absent.
   const [draft] = useState(loadDraft); // one-time localStorage read on mount
   const [form, setForm] = useState<FormShape>(
      (draft?.form as FormShape) ?? EMPTY_FORM
   );
   const [answers, setAnswers] = useState<DistributorQualifyingAnswers>(
      draft?.answers ?? emptyAnswers
   );

   // Wizard: step 1 = profile/docs/qualifying, step 2 = bundle + payment proof.
   const [step, setStep] = useState<1 | 2>(draft?.step ?? 1);
   const [selectedBundle, setSelectedBundle] = useState<CreditPackageTier | null>(
      draft?.selectedBundle ?? null
   );
   const [selectedAccount, setSelectedAccount] = useState<SelectedAccount>(
      draft?.selectedAccount ?? null
   );
   const [proofUrl, setProofUrl] = useState(draft?.proofUrl ?? '');
   const [uploading, setUploading] = useState(false);

   const { data: packages = [], isLoading: packagesLoading } = useQuery({
      queryKey: ['vendo', 'credit-package-tiers'],
      queryFn: getCreditPackageTiers,
   });
   // Distributors should start on a real tier — exclude the 1-credit base
   // package from the apply bundle picker (the goal is higher-tier volume).
   const sellableBundles = packages.filter((p) => p.credits > 1);
   const { data: paymentDetails } = useQuery({
      queryKey: ['vendo', 'credit-package-payment-details'],
      queryFn: getCreditPackagePaymentDetails,
   });
   const totalAccounts =
      (paymentDetails?.bankAccounts.length ?? 0) +
      (paymentDetails?.gcashAccounts.length ?? 0);

   const update = <K extends keyof FormShape>(key: K, value: FormShape[K]) =>
      setForm((prev) => ({ ...prev, [key]: value }));
   const toggleAnswer = (key: QualifyingKey) =>
      setAnswers((prev) => ({ ...prev, [key]: !prev[key] }));

   // PH locations cascade: region → province → city. Province is a UX-only
   // intermediate (we don't persist it on the application). Selected region
   // and city are stored as their human-readable names in form.region/city.
   const [regionCode, setRegionCode] = useState(draft?.regionCode ?? '');
   const [provinceCode, setProvinceCode] = useState(draft?.provinceCode ?? '');

   // Persist the in-progress application on every change so an accidental modal
   // close doesn't wipe it. Best-effort; cleared on successful submit.
   useEffect(() => {
      try {
         localStorage.setItem(
            DRAFT_KEY,
            JSON.stringify({
               form,
               answers,
               step,
               selectedBundle,
               selectedAccount,
               proofUrl,
               regionCode,
               provinceCode,
            })
         );
      } catch {
         /* storage full/disabled — draft persistence is best-effort */
      }
   }, [
      form,
      answers,
      step,
      selectedBundle,
      selectedAccount,
      proofUrl,
      regionCode,
      provinceCode,
   ]);
   const { data: regionList = [] } = useQuery({
      queryKey: ['ph-regions'],
      queryFn: regions,
      staleTime: Infinity,
   });
   const { data: provinceList = [] } = useQuery({
      queryKey: ['ph-provinces', regionCode],
      queryFn: () => provinces(regionCode),
      enabled: !!regionCode,
      staleTime: Infinity,
   });
   const { data: cityList = [] } = useQuery({
      queryKey: ['ph-cities', provinceCode],
      queryFn: () => cities(provinceCode),
      enabled: !!provinceCode,
      staleTime: Infinity,
   });

   const handleRegionChange = (code: string) => {
      setRegionCode(code);
      setProvinceCode('');
      const region = regionList.find((r) => r.region_code === code);
      update('region', region?.region_name ?? '');
      update('city', '');
   };
   const handleProvinceChange = (code: string) => {
      setProvinceCode(code);
      update('city', '');
   };
   const handleCityChange = (cityName: string) => {
      update('city', cityName);
   };

   const submitM = useMutation({
      mutationFn: (payload: SubmitDistributorApplicationPayload) =>
         submitDistributorApplication(payload),
      onSuccess: () => {
         try {
            localStorage.removeItem(DRAFT_KEY); // application submitted — drop the draft
         } catch {
            /* ignore */
         }
         queryClient.invalidateQueries({
            queryKey: ['vendo', 'distributor-applications', 'mine'],
         });
         toast.success('Application submitted — admin will review shortly.', {
            autoClose: 3000,
         });
         handleClose();
      },
      onError: (err: any) => {
         toast.error(err?.response?.data?.error ?? 'Failed to submit application');
      },
   });

   // Step 1 required fields. Returns the first missing key, or null if valid.
   const step1Missing = (): string | null => {
      const required = {
         displayName: form.displayName.trim(),
         businessName: form.businessName.trim(),
         region: form.region.trim(),
         city: form.city.trim(),
         contactPhone: form.contactPhone.trim(),
         facebookUrl: form.facebookUrl.trim(),
         govIdUrl: form.govIdUrl.trim(),
      };
      return (
         (Object.keys(required) as (keyof typeof required)[]).find(
            (k) => required[k] === ''
         ) ?? null
      );
   };

   const handleNext = () => {
      const missingKey = step1Missing();
      if (missingKey) {
         toast.error(`Please fill in ${missingKey}`);
         return;
      }
      setStep(2);
   };

   const handleSubmit = () => {
      // Re-guard step 1 in case the user jumped back/forth.
      const missingKey = step1Missing();
      if (missingKey) {
         toast.error(`Please fill in ${missingKey}`);
         setStep(1);
         return;
      }
      if (uploading) {
         toast.info('Wait for the screenshot to finish uploading.');
         return;
      }
      if (!selectedBundle) {
         toast.error('Pick a credit bundle to start with.');
         return;
      }
      if (!selectedAccount) {
         toast.error('Pick where you sent the payment.');
         return;
      }
      if (!proofUrl) {
         toast.error('Upload a screenshot of your payment.');
         return;
      }
      const payload: SubmitDistributorApplicationPayload = {
         displayName: form.displayName.trim(),
         businessName: form.businessName.trim(),
         region: form.region.trim(),
         city: form.city.trim(),
         contactPhone: form.contactPhone.trim(),
         facebookUrl: form.facebookUrl.trim(),
         govIdUrl: form.govIdUrl.trim(),
         mayorsPermitUrl: form.mayorsPermitUrl.trim() || undefined,
         bir2303Url: form.bir2303Url.trim() || undefined,
         proofOfAddressUrl: form.proofOfAddressUrl.trim() || undefined,
         qualifyingAnswers: answers,
         bundleName: selectedBundle.name,
         paymentMethod:
            selectedAccount.kind === 'gcash' ? 'gcash' : 'bank-transfer',
         paymentProofUrl: proofUrl,
      };
      submitM.mutate(payload);
   };

   const yesCount = QUALIFYING_QUESTIONS.filter((q) => answers[q.key]).length;
   const approvalChance = computeApprovalScore({
      facebookUrl: form.facebookUrl.trim(),
      mayorsPermitUrl: form.mayorsPermitUrl.trim(),
      bir2303Url: form.bir2303Url.trim(),
      proofOfAddressUrl: form.proofOfAddressUrl.trim(),
      qualifyingAnswers: answers,
   });

   if (pendingExisting) {
      return (
         <Wrapper>
            <Intro>
               <IntroTitle>You already have a pending application</IntroTitle>
               <IntroText>
                  Submitted{' '}
                  {new Date(pendingExisting.createdAt).toLocaleDateString('en-PH')} for{' '}
                  <strong>{pendingExisting.displayName}</strong>. Wait for admin
                  review — you'll be notified once it's approved or rejected.
               </IntroText>
            </Intro>
            <BtnRow>
               <div onClick={handleClose}>
                  <Btn title="Close" type="normal" size="small" />
               </div>
            </BtnRow>
         </Wrapper>
      );
   }

   return (
      <Wrapper>
         <Intro>
            <IntroTitle>
               {isUpgrade
                  ? 'Upgrade to a SmartFi Distributor'
                  : 'Apply as a SmartFi Distributor'}
            </IntroTitle>
            <IntroText>
               {step === 1
                  ? 'Approved distributors buy credit packages at distributor pricing and resell to vendo owners in their region. Admin reviews each application manually — typical turnaround is 1–3 business days.'
                  : 'Pick your starting credit bundle and upload your payment proof. We hold this with your application — your wallet is credited the moment admin approves, so you can start selling right away.'}
            </IntroText>
         </Intro>

         {isUpgrade && step === 1 && (
            <UpgradeNotice>
               <UpgradeTitle>You are a reseller — this changes your account</UpgradeTitle>
               <UpgradeList>
                  <UpgradeKeep>
                     Your credits and your points come with you. They belong to your
                     account, not to your reseller role.
                  </UpgradeKeep>
                  <UpgradeKeep>
                     Your public link keeps working, and your past sales stay in your
                     records.
                  </UpgradeKeep>
                  <UpgradeEnds>
                     You stop being a reseller. Your link to your distributor ends and
                     you buy your credits from SmartFi instead.
                  </UpgradeEnds>
                  <UpgradeEnds>
                     Anyone still waiting for credits from you is told to ask you again
                     as a distributor.
                  </UpgradeEnds>
               </UpgradeList>
               <UpgradeFoot>
                  Nothing changes until admin approves. If we turn the application down,
                  you stay a reseller exactly as you are now.
               </UpgradeFoot>
            </UpgradeNotice>
         )}

         <StepBar>
            <StepDot $active={step === 1} $done={step > 1}>
               <StepNum>1</StepNum> Profile
            </StepDot>
            <StepLine />
            <StepDot $active={step === 2} $done={false}>
               <StepNum>2</StepNum> Bundle &amp; payment
            </StepDot>
         </StepBar>

         {step === 1 && (
            <>
         <Section>
            <SectionTitle>Business Profile</SectionTitle>
            <Field>
               <Lbl>Display name *</Lbl>
               <Hint>Public-facing name shown on the partner marketplace</Hint>
               <TextInput
                  value={form.displayName}
                  placeholder="e.g. Juan Tech Hub"
                  onChange={(e) => update('displayName', e.target.value)}
               />
            </Field>
            <Row>
               <Field>
                  <Lbl>Legal business name *</Lbl>
                  <TextInput
                     value={form.businessName}
                     placeholder="e.g. Juan Tech Hub Trading Inc."
                     onChange={(e) => update('businessName', e.target.value)}
                  />
               </Field>
               <Field>
                  <Lbl>Contact phone *</Lbl>
                  <TextInput
                     value={form.contactPhone}
                     placeholder="+63 917 555 1234"
                     onChange={(e) => update('contactPhone', e.target.value)}
                  />
               </Field>
            </Row>
            <Field>
               <Lbl>Region *</Lbl>
               <SelectInput
                  value={regionCode}
                  onChange={(e) => handleRegionChange(e.target.value)}
               >
                  <option value="">Select region…</option>
                  {regionList.map((r) => (
                     <option key={r.region_code} value={r.region_code}>
                        {r.region_name}
                     </option>
                  ))}
               </SelectInput>
            </Field>
            <Row>
               <Field>
                  <Lbl>Province *</Lbl>
                  <SelectInput
                     value={provinceCode}
                     onChange={(e) => handleProvinceChange(e.target.value)}
                     disabled={!regionCode}
                  >
                     <option value="">
                        {regionCode ? 'Select province…' : 'Pick region first'}
                     </option>
                     {provinceList.map((p) => (
                        <option key={p.province_code} value={p.province_code}>
                           {p.province_name}
                        </option>
                     ))}
                  </SelectInput>
               </Field>
               <Field>
                  <Lbl>City / Municipality *</Lbl>
                  <SelectInput
                     value={form.city}
                     onChange={(e) => handleCityChange(e.target.value)}
                     disabled={!provinceCode}
                  >
                     <option value="">
                        {provinceCode ? 'Select city…' : 'Pick province first'}
                     </option>
                     {cityList.map((c) => (
                        <option key={c.city_code} value={c.city_name}>
                           {c.city_name}
                        </option>
                     ))}
                  </SelectInput>
               </Field>
            </Row>
            <Field>
               <Lbl>Personal Facebook URL *</Lbl>
               <Hint>Used for identity verification — link to your real account.</Hint>
               <TextInput
                  value={form.facebookUrl}
                  placeholder="https://facebook.com/your.username"
                  onChange={(e) => update('facebookUrl', e.target.value)}
               />
            </Field>
         </Section>

         <Section>
            <SectionTitle>Required Documents</SectionTitle>
            <Hint>
               Click to upload or drag-and-drop a clear photo or scan of each document.
            </Hint>
            {REQUIRED_DOCS.map(({ key, label, hint }) => (
               <Field key={key}>
                  <Lbl>{label} *</Lbl>
                  <SubHint>{hint}</SubHint>
                  <FileUploader
                     value={String(form[key] ?? '')}
                     onChange={(url) => update(key, url)}
                  />
               </Field>
            ))}
         </Section>

         <Section>
            <SectionTitleRow>
               <SectionTitle>Qualifying Questions</SectionTitle>
               <QualScorePill>
                  {yesCount}/{QUALIFYING_QUESTIONS.length} yes
               </QualScorePill>
            </SectionTitleRow>
            <Hint>
               Check the boxes that apply to you. Honest answers help admin match you
               with the right opportunities — leaving boxes unchecked won't disqualify
               you outright.
            </Hint>
            <QualList>
               {QUALIFYING_QUESTIONS.map(({ key, label }) => (
                  <QualRow key={key} $checked={answers[key]}>
                     <QualCheckbox
                        type="checkbox"
                        checked={answers[key]}
                        onChange={() => toggleAnswer(key)}
                     />
                     <QualLbl>{label}</QualLbl>
                  </QualRow>
               ))}
            </QualList>
         </Section>

         <Section>
            <SectionTitleRow>
               <SectionTitleGroup>
                  <SectionTitle>Boost Your Approval</SectionTitle>
                  <OptionalPill>OPTIONAL</OptionalPill>
               </SectionTitleGroup>
               <ApprovalScorePill $score={approvalChance}>
                  ~{approvalChance}% chance of approval
               </ApprovalScorePill>
            </SectionTitleRow>
            <SkipCallout>
               <SkipIcon>✓</SkipIcon>
               <SkipText>
                  <strong>You can skip this section</strong> and submit now. Adding
                  any of these docs is optional — it just boosts your approval odds.
               </SkipText>
            </SkipCallout>
            {OPTIONAL_DOCS.map(({ key, label, hint }) => (
               <Field key={key}>
                  <LblRow>
                     <Lbl>{label}</Lbl>
                     <OptionalPillInline>OPTIONAL · SKIP OK</OptionalPillInline>
                  </LblRow>
                  <SubHint>{hint}</SubHint>
                  <FileUploader
                     value={String(form[key] ?? '')}
                     onChange={(url) => update(key, url)}
                  />
               </Field>
            ))}
         </Section>
            </>
         )}

         {step === 2 && (
            <>
               <Section>
                  <SectionTitle>Choose your starting bundle *</SectionTitle>
                  <Hint>
                     You pay for this now and your wallet is credited when admin
                     approves your application. Bigger bundles cost less per credit.
                  </Hint>
                  {packagesLoading && <SkeletonLines lines={3} />}
                  {!packagesLoading && sellableBundles.length === 0 && (
                     <SubHint>No bundles available — contact admin.</SubHint>
                  )}
                  <BundleList>
                     {sellableBundles.map((pkg, idx) => {
                        const isSelected = selectedBundle?.name === pkg.name;
                        // Featured slot is the middle of the list (visual
                        // emphasis) — same rule as the Partners credit store.
                        const featured =
                           sellableBundles.length >= 4 &&
                           idx === Math.floor(sellableBundles.length / 2);
                        // Tier theming is bundle-name keyed; unknown names fall
                        // back to "Gold" so the accent still renders.
                        const themeKey = [
                           'Starter',
                           'Bronze',
                           'Silver',
                           'Gold',
                           'Diamond',
                           'Legendary',
                        ].includes(pkg.name)
                           ? pkg.name
                           : 'Gold';
                        const pricePerCredit = Math.round(pkg.pricePerCredit);
                        return (
                           <BundleRow
                              key={pkg.name}
                              type="button"
                              $tier={themeKey}
                              $selected={isSelected}
                              onClick={() => setSelectedBundle(pkg)}
                           >
                              <BundleIcon $tier={themeKey}>
                                 {pkg.icon || '🎟️'}
                              </BundleIcon>
                              <BundleBody>
                                 <BundleTop>
                                    <BundleName>{pkg.name}</BundleName>
                                    {featured && (
                                       <PopularPill>★ Most Popular</PopularPill>
                                    )}
                                    <BundleCredits>{pkg.credits} credits</BundleCredits>
                                    <BundleAmount>
                                       ₱{pkg.amount.toLocaleString()}
                                    </BundleAmount>
                                    <BundlePer>₱{pricePerCredit}/credit</BundlePer>
                                 </BundleTop>
                                 {pkg.savings || pkg.bonusCredits || pkg.points ? (
                                    <BundleBadges>
                                       {pkg.savings ? (
                                          <SaveBadge>
                                             save ₱{pkg.savings.toLocaleString()}
                                          </SaveBadge>
                                       ) : null}
                                       {pkg.bonusCredits ? (
                                          <BonusBadge>
                                             🔥 +{pkg.bonusCredits.toLocaleString()} bonus
                                          </BonusBadge>
                                       ) : null}
                                       {pkg.points ? (
                                          <PointsBadge>
                                             ✨ earn {pkg.points.toLocaleString()} pts
                                          </PointsBadge>
                                       ) : null}
                                    </BundleBadges>
                                 ) : null}
                              </BundleBody>
                              <BundleRadio $tier={themeKey} $selected={isSelected}>
                                 {isSelected ? '✓' : ''}
                              </BundleRadio>
                           </BundleRow>
                        );
                     })}
                  </BundleList>
               </Section>

               <Section>
                  <SectionTitle>Send payment, then upload proof *</SectionTitle>
                  {totalAccounts === 0 ? (
                     <SkipCallout>
                        <SkipText>
                           SmartFi hasn't set up payment accounts yet. Contact support
                           before sending any money.
                        </SkipText>
                     </SkipCallout>
                  ) : (
                     <AccountList>
                        {paymentDetails?.bankAccounts.map((b, idx) => {
                           const isSel =
                              selectedAccount?.kind === 'bank' &&
                              selectedAccount.index === idx;
                           return (
                              <AccountOption
                                 key={`bank-${idx}`}
                                 type="button"
                                 $selected={isSel}
                                 onClick={() =>
                                    setSelectedAccount({ kind: 'bank', index: idx })
                                 }
                              >
                                 <AcctType>🏦 Bank</AcctType>
                                 <AcctBody>
                                    <AcctName>{b.bankName}</AcctName>
                                    <AcctMeta>
                                       <Mono>{b.accountNumber}</Mono> · {b.accountName}
                                    </AcctMeta>
                                 </AcctBody>
                                 <AcctRadio $selected={isSel} />
                              </AccountOption>
                           );
                        })}
                        {paymentDetails?.gcashAccounts.map((g, idx) => {
                           const isSel =
                              selectedAccount?.kind === 'gcash' &&
                              selectedAccount.index === idx;
                           return (
                              <AccountOption
                                 key={`gcash-${idx}`}
                                 type="button"
                                 $selected={isSel}
                                 onClick={() =>
                                    setSelectedAccount({ kind: 'gcash', index: idx })
                                 }
                              >
                                 <AcctType>📱 GCash</AcctType>
                                 <AcctBody>
                                    <AcctName>{g.name}</AcctName>
                                    <AcctMeta>
                                       <Mono>{g.number}</Mono>
                                    </AcctMeta>
                                 </AcctBody>
                                 <AcctRadio $selected={isSel} />
                              </AccountOption>
                           );
                        })}
                     </AccountList>
                  )}
                  {paymentDetails?.paymentInstructions && (
                     <SubHint>{paymentDetails.paymentInstructions}</SubHint>
                  )}
                  <Field>
                     <Lbl>Payment screenshot *</Lbl>
                     <SubHint>
                        A clear photo or screenshot of your payment confirmation.
                     </SubHint>
                     <FileUploader
                        value={proofUrl}
                        onChange={setProofUrl}
                        onUploadingChange={setUploading}
                     />
                  </Field>
               </Section>

               <Disclaimer>
                  By submitting you agree that the information provided is accurate.
                  Your wallet is credited <strong>after admin verifies</strong> the
                  payment. If your application is rejected, the payment is refunded —
                  no credits are released until approval.
               </Disclaimer>
            </>
         )}

         <BtnRow>
            {step === 1 ? (
               <>
                  <div onClick={handleClose}>
                     <Btn title="Cancel" type="outline" size="small" />
                  </div>
                  <div onClick={handleNext}>
                     <Btn
                        title="Next · Bundle & payment"
                        type="normal"
                        size="small"
                        bgcolor={colors.PRIMARY}
                        textcolor={colors.WHITE}
                     />
                  </div>
               </>
            ) : (
               <>
                  <div onClick={() => setStep(1)}>
                     <Btn title="Back" type="outline" size="small" />
                  </div>
                  <div onClick={handleSubmit}>
                     <Btn
                        title={
                           submitM.isPending
                              ? 'Submitting…'
                              : uploading
                                ? 'Uploading…'
                                : !selectedBundle
                                  ? 'Pick a bundle first'
                                  : !proofUrl
                                    ? 'Upload payment proof'
                                    : 'Submit Application'
                        }
                        type={
                           submitM.isPending || uploading ? 'disabled' : 'normal'
                        }
                        size="small"
                        bgcolor={
                           submitM.isPending || uploading
                              ? colors.GRAY10
                              : colors.PRIMARY
                        }
                        textcolor={
                           submitM.isPending || uploading
                              ? colors.GRAY1
                              : colors.WHITE
                        }
                     />
                  </div>
               </>
            )}
         </BtnRow>
      </Wrapper>
   );
};

const Wrapper = styled.div`
   width: 560px;
   max-width: 100%;
   display: flex;
   flex-direction: column;
   gap: 14px;
   max-height: 70vh;
   overflow-y: auto;
   @media (max-width: 768px) { width: 100%; max-height: 80vh; }
`;

const Intro = styled.div`
   padding: 10px 12px;
   border-radius: 8px;
   background: var(--app-brand-tint);
`;

const IntroTitle = styled.p`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
   margin-bottom: 4px;
`;

const IntroText = styled.p`
   font-size: 12px;
   color: ${colors.GRAY3};
   line-height: 1.5;
`;

/* Shown only to a reseller: what the upgrade keeps and what it ends. */
const UpgradeNotice = styled.div`
   padding: 10px 12px;
   border-radius: 8px;
   border: 1px solid ${colors.WARNING};
   background: ${colors.WARNING}12;
   display: flex;
   flex-direction: column;
   gap: 8px;
`;

const UpgradeTitle = styled.p`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const UpgradeList = styled.ul`
   display: flex;
   flex-direction: column;
   gap: 6px;
   list-style: none;
`;

const UpgradeLine = styled.li`
   font-size: 12px;
   line-height: 1.5;
   color: ${colors.GRAY3};
   padding-left: 20px;
   position: relative;
   &::before {
      position: absolute;
      left: 0;
      top: 0;
   }
`;

const UpgradeKeep = styled(UpgradeLine)`
   &::before {
      content: '✓';
      color: ${colors.SUCCESS};
      font-weight: 700;
   }
`;

const UpgradeEnds = styled(UpgradeLine)`
   &::before {
      content: '→';
      color: ${colors.WARNING};
      font-weight: 700;
   }
`;

const UpgradeFoot = styled.p`
   font-size: 11px;
   color: ${colors.GRAY3};
   line-height: 1.5;
   font-style: italic;
`;

const Section = styled.section`
   display: flex;
   flex-direction: column;
   gap: 10px;
   padding-top: 8px;
   border-top: 1px solid ${colors.GRAY13};
`;

const SectionTitleRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 8px;
   flex-wrap: wrap;
`;

const QualScorePill = styled.span`
   font-size: 11px;
   font-weight: 800;
   padding: 3px 10px;
   border-radius: 999px;
   background: ${colors.GRAY13};
   color: ${colors.GRAY3};
`;

const QualList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const QualRow = styled.label<{ $checked: boolean }>`
   display: flex;
   align-items: flex-start;
   gap: 8px;
   padding: 8px 10px;
   border: 1px solid
      ${({ $checked }) => ($checked ? `${colors.PRIMARY}55` : colors.GRAY12)};
   background: ${({ $checked }) =>
      $checked ? `${colors.PRIMARY}08` : 'var(--app-surface)'};
   border-radius: 6px;
   cursor: pointer;
   &:hover { border-color: ${colors.PRIMARY}; }
`;

const QualCheckbox = styled.input`
   margin-top: 2px;
   flex-shrink: 0;
   cursor: pointer;
   accent-color: ${colors.PRIMARY};
`;

const QualLbl = styled.span`
   font-size: 12px;
   color: ${colors.MAIN_DARK};
   line-height: 1.4;
`;

const ApprovalScorePill = styled.span<{ $score: number }>`
   font-size: 11px;
   font-weight: 800;
   padding: 3px 10px;
   border-radius: 999px;
   background: ${({ $score }) =>
      $score >= 90
         ? `${colors.SUCCESS}18`
         : $score >= 75
         ? `${colors.WARNING}18`
         : `${colors.GRAY13}`};
   color: ${({ $score }) =>
      $score >= 90 ? colors.SUCCESS : $score >= 75 ? colors.WARNING : colors.GRAY3};
`;

const SectionTitle = styled.h4`
   font-size: 12px;
   font-weight: 800;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.06em;
`;

const SectionTitleGroup = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   flex-wrap: wrap;
`;

const OptionalPill = styled.span`
   font-size: 10px;
   font-weight: 800;
   letter-spacing: 0.08em;
   padding: 3px 8px;
   border-radius: 999px;
   background: ${colors.SUCCESS}18;
   color: ${colors.SUCCESS};
   border: 1px solid ${colors.SUCCESS}40;
`;

const LblRow = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   flex-wrap: wrap;
`;

const OptionalPillInline = styled.span`
   font-size: 9px;
   font-weight: 800;
   letter-spacing: 0.08em;
   padding: 2px 7px;
   border-radius: 999px;
   background: ${colors.SUCCESS}18;
   color: ${colors.SUCCESS};
   border: 1px solid ${colors.SUCCESS}40;
`;

const SkipCallout = styled.div`
   display: flex;
   align-items: flex-start;
   gap: 10px;
   padding: 10px 12px;
   background: ${colors.SUCCESS}0d;
   border: 1px solid ${colors.SUCCESS}40;
   border-radius: 8px;
`;

const SkipIcon = styled.span`
   font-size: 14px;
   font-weight: 800;
   color: ${colors.SUCCESS};
   line-height: 1.4;
`;

const SkipText = styled.p`
   font-size: 12px;
   color: ${colors.MAIN_DARK};
   line-height: 1.45;
   & strong {
      color: ${colors.SUCCESS};
   }
`;

const Row = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 10px;
   @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const Field = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const Lbl = styled.label`
   font-size: 12px;
   font-weight: 600;
   color: ${colors.GRAY1};
`;

const Hint = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.4;
`;

const SubHint = styled.p`
   font-size: 10px;
   color: ${colors.GRAY5};
`;

const TextInput = styled.input`
   padding: 8px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 13px;
   font-family: inherit;
   color: ${colors.MAIN_DARK};
   background: var(--app-surface);
   outline: none;
   &:focus { border-color: ${colors.PRIMARY}; }
`;

const SelectInput = styled.select`
   padding: 8px 10px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 6px;
   font-size: 13px;
   font-family: inherit;
   color: ${colors.MAIN_DARK};
   background: var(--app-surface);
   outline: none;
   cursor: pointer;
   &:focus { border-color: ${colors.PRIMARY}; }
   &:disabled { cursor: not-allowed; opacity: 0.55; }
`;

const Disclaimer = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   line-height: 1.5;
   padding: 8px 10px;
   background: ${colors.PRIMARY}08;
   border-left: 3px solid ${colors.PRIMARY};
   border-radius: 4px;
`;

const BtnRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 10px;
   margin-top: 4px;
`;

const StepBar = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
`;

const StepDot = styled.div<{ $active: boolean; $done: boolean }>`
   display: flex;
   align-items: center;
   gap: 6px;
   font-size: 11px;
   font-weight: 800;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   color: ${({ $active, $done }) =>
      $active || $done ? colors.PRIMARY : colors.GRAY5};
`;

const StepNum = styled.span`
   display: inline-flex;
   align-items: center;
   justify-content: center;
   width: 18px;
   height: 18px;
   border-radius: 999px;
   background: currentColor;
   color: ${colors.WHITE};
   font-size: 10px;
   font-weight: 900;
`;

const StepLine = styled.div`
   flex: 1;
   height: 1px;
   background: ${colors.GRAY12};
`;

// ── Compact bundle-row picker ──
// Tier theming keyed by bundle name; a subtle left accent bar + colored ring
// on select. Unknown names fall back to "Gold".
const tierAccent = (tier: string) => {
   switch (tier) {
      case 'Starter':
         return '#64748b';
      case 'Bronze':
         return '#b45309';
      case 'Silver':
         return '#64748b';
      case 'Gold':
         return '#d97706';
      case 'Diamond':
         return '#0891b2';
      case 'Legendary':
         return '#a21caf';
      default:
         return '#d97706';
   }
};

const BundleList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
`;

const BundleRow = styled.button<{ $tier: string; $selected: boolean }>`
   position: relative;
   display: flex;
   align-items: center;
   gap: 12px;
   width: 100%;
   padding: 10px 12px 10px 14px;
   border: 1px solid
      ${({ $tier, $selected }) => ($selected ? tierAccent($tier) : colors.GRAY12)};
   border-left: 4px solid ${({ $tier }) => tierAccent($tier)};
   background: ${({ $tier, $selected }) =>
      $selected ? `${tierAccent($tier)}0f` : 'var(--app-surface)'};
   border-radius: 8px;
   cursor: pointer;
   text-align: left;
   font-family: inherit;
   transition: all 120ms ease;
   box-shadow: ${({ $tier, $selected }) =>
      $selected ? `0 0 0 2px ${tierAccent($tier)}55` : 'none'};
   &:hover {
      border-color: ${({ $tier }) => tierAccent($tier)};
      background: ${({ $tier }) => `${tierAccent($tier)}0a`};
   }
`;

const BundleIcon = styled.span<{ $tier: string }>`
   font-size: 22px;
   flex-shrink: 0;
   width: 40px;
   height: 40px;
   display: grid;
   place-items: center;
   border-radius: 999px;
   background: radial-gradient(
      circle,
      ${({ $tier }) => `${tierAccent($tier)}22`},
      ${({ $tier }) => `${tierAccent($tier)}08`}
   );
   box-shadow: 0 0 10px -2px ${({ $tier }) => `${tierAccent($tier)}66`};
`;

const BundleBody = styled.div`
   flex: 1;
   min-width: 0;
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const BundleTop = styled.div`
   display: flex;
   align-items: baseline;
   gap: 8px;
   flex-wrap: wrap;
`;

const BundleName = styled.span`
   font-size: 13px;
   font-weight: 800;
   color: ${colors.MAIN_DARK};
`;

const BundleCredits = styled.span`
   font-size: 15px;
   font-weight: 900;
   color: ${colors.GRAY_DARK};
   letter-spacing: -0.01em;
`;

const BundleAmount = styled.span`
   font-size: 13px;
   font-weight: 800;
   color: ${colors.PRIMARY};
`;

const BundlePer = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
`;

const PopularPill = styled.span`
   font-size: 9px;
   font-weight: 900;
   color: #fff;
   background: linear-gradient(135deg, #f43f5e, #ec4899);
   padding: 2px 8px;
   border-radius: 999px;
   letter-spacing: 0.04em;
   text-transform: uppercase;
   white-space: nowrap;
   box-shadow: 0 2px 6px rgba(236, 72, 153, 0.35);
`;

const BundleBadges = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   flex-wrap: wrap;
`;

const SaveBadge = styled.span`
   font-size: 10px;
   font-weight: 800;
   color: ${colors.SUCCESS};
   background: ${colors.SUCCESS}15;
   padding: 2px 8px;
   border-radius: 999px;
   text-transform: uppercase;
   letter-spacing: 0.04em;
   white-space: nowrap;
`;

const BonusBadge = styled.span`
   font-size: 10px;
   font-weight: 900;
   color: #fff;
   background: #b91c1c;
   padding: 2px 8px;
   border-radius: 999px;
   letter-spacing: 0.03em;
   white-space: nowrap;
`;

const PointsBadge = styled.span`
   font-size: 10px;
   font-weight: 800;
   color: #fff;
   background: linear-gradient(135deg, #f59e0b, #ef4444);
   padding: 2px 8px;
   border-radius: 999px;
   white-space: nowrap;
`;

const BundleRadio = styled.span<{ $tier: string; $selected: boolean }>`
   flex-shrink: 0;
   width: 20px;
   height: 20px;
   border-radius: 999px;
   display: grid;
   place-items: center;
   font-size: 12px;
   font-weight: 900;
   color: #fff;
   border: 2px solid
      ${({ $tier, $selected }) => ($selected ? tierAccent($tier) : colors.GRAY11)};
   background: ${({ $tier, $selected }) =>
      $selected ? tierAccent($tier) : 'transparent'};
`;

const AccountList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
`;

const AccountOption = styled.button<{ $selected: boolean }>`
   display: flex;
   align-items: center;
   gap: 10px;
   padding: 10px 12px;
   border: 1px solid ${({ $selected }) =>
      $selected ? colors.PRIMARY : colors.GRAY12};
   background: ${({ $selected }) =>
      $selected ? `${colors.PRIMARY}08` : 'var(--app-surface)'};
   border-radius: 8px;
   cursor: pointer;
   text-align: left;
   font-family: inherit;
   &:hover { border-color: ${colors.PRIMARY}; }
`;

const AcctType = styled.span`
   font-size: 11px;
   font-weight: 800;
   flex-shrink: 0;
   width: 64px;
`;

const AcctBody = styled.div`
   flex: 1;
   min-width: 0;
`;

const AcctName = styled.p`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const AcctMeta = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   margin-top: 2px;
`;

const Mono = styled.span`
   font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
   color: ${colors.MAIN_DARK};
`;

const AcctRadio = styled.span<{ $selected: boolean }>`
   width: 16px;
   height: 16px;
   border-radius: 999px;
   flex-shrink: 0;
   border: 2px solid ${({ $selected }) =>
      $selected ? colors.PRIMARY : colors.GRAY11};
   background: ${({ $selected }) =>
      $selected ? colors.PRIMARY : 'transparent'};
   box-shadow: ${({ $selected }) =>
      $selected ? `inset 0 0 0 2px var(--app-surface)` : 'none'};
`;

export default DistributorApply;
